/**
 * TaskScoreDistribution — every task in a group plotted easiest →
 * hardest (score descending), the analysis page's headline chart.
 * Modeled on the Mercor console's distribution: smooth continuous
 * lines whose color follows the score ramp (green → amber → red), rank
 * ticks along the x-axis, hard/easy/scored badges and the running
 * average in the header. Supports multiple series (highest / average /
 * lowest), each sorted into its own burndown curve. Dots are invisible
 * hover targets — hovering opens the "tasks near cursor" popover (plus
 * a visible dot), clicking opens the task. The popover is
 * component-owned state (NOT a recharts tooltip) so it stays put while
 * the pointer travels into it.
 */

import { memo, useCallback, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  CartesianGrid,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  XAxis,
  YAxis,
  ZAxis,
} from 'recharts'

import { scoreTone, taskScoreFor } from '@/components/evals/analysis'
import { FULL_SCALE, scoreDomain } from '@/components/evals/climb'
import { formatScore } from '@/components/evals/format'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import type { LedgerTaskDetails } from '@/components/evals/AnalysisLedger'
import type { AnalysisTask, AnalysisTaskDifficulty } from '@/api/generated/model'

/** One plotted statistic: its label and how to score a task under it. */
export interface DistributionSeries {
  key: string
  label: string
  scoreFor: (taskSlug: string, model: string | null) => number | null
}

interface TaskScoreDistributionProps {
  tasks: AnalysisTask[]
  /** Selected model filter; null plots each task's headline score. */
  model: string | null
  /** Where a task's dot navigates on click — caller owns the route family. */
  taskPath: (taskSlug: string) => string
  /** Optional per-task metadata (world/section) for the hover popover. */
  details?: Map<string, LedgerTaskDetails>
  /**
   * Series to plot (highest / average / lowest). Defaults to a single
   * headline-score series for surfaces without the toggle.
   */
  series?: DistributionSeries[]
  /** Subtitle under the chart title; describes what the y-value is. */
  subtitle?: string
}

interface DistributionPoint {
  x: number
  score: number
  slug: string
  displayName: string
  difficulty: AnalysisTaskDifficulty
  runCount: number
}

function toPoints(
  tasks: AnalysisTask[],
  model: string | null,
  scoreFor: (taskSlug: string, model: string | null) => number | null
): DistributionPoint[] {
  const scored = tasks.flatMap((task) => {
    const score = scoreFor(task.slug, model)
    return score === null ? [] : [{ task, score }]
  })
  scored.sort((a, b) => b.score - a.score || a.task.slug.localeCompare(b.task.slug))
  return scored.map(({ task, score }, index) => ({
    x: index + 1,
    score: score * 100,
    slug: task.slug,
    displayName: task.display_name,
    difficulty: task.difficulty,
    runCount: task.run_count,
  }))
}

/** Rank ticks like the console: 1, 100, 200, …, N. */
function rankTicks(count: number): number[] {
  const ticks = [1]
  for (let tick = 100; tick < count; tick += 100) ticks.push(tick)
  ticks.push(count)
  return ticks
}

/** How many neighbors to show around the cursor's rank. */
const NEIGHBOR_WINDOW = 7

/** Popover width (px) — must match the w-80 class on the popover. */
const POPOVER_WIDTH = 320

/** Grace period before the popover hides, so the pointer can reach it. */
const POPOVER_HIDE_DELAY_MS = 200

/**
 * "Tasks near cursor" popover, console style: the hovered rank plus its
 * neighbors within the hovered series, each with name, #rank, score,
 * world/section, and trace count. Rows navigate to the task.
 */
function DistributionPopover({
  datum,
  points,
  details,
  headerLabel,
  onOpenTask,
}: {
  datum: DistributionPoint
  points: DistributionPoint[]
  details: Map<string, LedgerTaskDetails> | undefined
  headerLabel: string
  onOpenTask: (taskSlug: string) => void
}): React.JSX.Element {
  const center = datum.x - 1
  const half = Math.floor(NEIGHBOR_WINDOW / 2)
  const start = Math.max(0, Math.min(center - half, points.length - NEIGHBOR_WINDOW))
  const neighbors = points.slice(start, start + NEIGHBOR_WINDOW)

  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className="w-80 rounded-lg border border-border bg-card p-3 shadow-md"
    >
      <div className="flex items-baseline justify-between gap-3 border-b border-border pb-2">
        <p className="text-xs text-muted-foreground">{headerLabel} tasks near cursor</p>
        <p className="font-mono text-xs text-muted-foreground">rank {datum.x}</p>
      </div>
      <div className="mt-2 space-y-2.5">
        {neighbors.map((point) => {
          const meta = details?.get(point.slug)
          const parts = [
            `${point.score.toFixed(1)}%`,
            ...(meta !== undefined && meta.world !== '' ? [meta.world] : []),
            ...(meta !== undefined && meta.section !== '' ? [meta.section] : []),
            `${point.runCount.toString()} ${point.runCount === 1 ? 'trace' : 'traces'}`,
          ]
          return (
            <button
              key={point.slug}
              type="button"
              onClick={() => {
                onOpenTask(point.slug)
              }}
              className={`-mx-1.5 block w-[calc(100%+0.75rem)] rounded-md px-1.5 py-0.5 text-left hover:bg-muted ${point.x === datum.x ? '' : 'opacity-80'}`}
            >
              <div className="flex items-baseline justify-between gap-3">
                <p className="truncate text-sm font-medium text-foreground">{point.displayName}</p>
                <p className="shrink-0 font-mono text-xs text-muted-foreground">#{point.x}</p>
              </div>
              <p className="truncate text-xs text-muted-foreground">{parts.join(' - ')}</p>
            </button>
          )
        })}
      </div>
    </div>
  )
}

/**
 * Gradient stops sampled along a curve — one <linearGradient> across
 * the full x range beats one series per segment (a 480-task benchmark
 * would otherwise mount 480 recharts series). ~25 samples is plenty.
 */
function gradientStops(points: DistributionPoint[], top: number): { offset: string; color: string }[] {
  const sampleCount = Math.min(25, points.length)
  const stops: { offset: string; color: string }[] = []
  for (let sample = 0; sample < sampleCount; sample++) {
    const index = Math.round((sample / (sampleCount - 1)) * (points.length - 1))
    const point = points[index]
    if (point === undefined) {
      throw new Error(`unreachable: gradient sample ${sample.toString()} out of range`)
    }
    stops.push({
      offset: `${((sample / (sampleCount - 1)) * 100).toFixed(1)}%`,
      color: scoreTone(point.score / top),
    })
  }
  return stops
}

interface PlottedSeries {
  def: DistributionSeries
  points: DistributionPoint[]
}

/**
 * Top of the score axis, in the percent space these points live in. Full scale
 * unless the data exceeds it, in which case the padded data max — scores are
 * unbounded now, and a fixed 100 would plot anything above full scale off the
 * top of the frame, where recharts clips it and the dot simply vanishes.
 *
 * It never drops BELOW full scale, which is what keeps this chart absolute.
 * Both the axis and `scoreTone` read against this top, and `scoreTone` is an
 * absolute red-to-green ramp: the legend prints fixed `scoreTone(0.1)` /
 * `scoreTone(0.9)` swatches, and the hard/easy badges come from the API's own
 * cutoffs. Shrinking the top to a low-scoring group's max would tint every
 * task greener than its badge says it is, and a rate group whose best task
 * falls short of 100 would silently re-grade its whole curve.
 */
function plotTop(plotted: PlottedSeries[]): number {
  const dataMax = Math.max(0, ...plotted.flatMap(({ points }) => points.map((p) => p.score)))
  return Math.max(FULL_SCALE, scoreDomain(dataMax))
}

/**
 * The recharts block, memoized: hover state lives OUTSIDE, so opening
 * and moving the popover never re-renders the chart (500+ dots × up to
 * three series made every hover visibly laggy otherwise).
 */
const DistributionChart = memo(function DistributionChart({
  plotted,
}: {
  plotted: PlottedSeries[]
}): React.JSX.Element {
  const primary = plotted[0]
  if (primary === undefined) {
    throw new Error('unreachable: DistributionChart requires at least one series')
  }
  const top = plotTop(plotted)
  return (
    <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
      <ScatterChart accessibilityLayer={false} margin={{ top: PLOT_TOP, right: PLOT_RIGHT_PAD, bottom: 0, left: 0 }}>
        <CartesianGrid stroke="hsl(var(--border) / 0.6)" vertical={false} strokeDasharray="4 4" />
        <XAxis
          dataKey="x"
          name="Task rank"
          type="number"
          domain={[1, primary.points.length]}
          ticks={rankTicks(primary.points.length)}
          tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
          tickLine={false}
          axisLine={false}
          height={XAXIS_HEIGHT}
        />
        <YAxis
          dataKey="score"
          name="Score"
          domain={[0, top]}
          ticks={[0, 0.25, 0.5, 0.75, 1].map((fraction) => fraction * top)}
          tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
          tickLine={false}
          axisLine={false}
          width={YAXIS_WIDTH}
        />
        <ZAxis range={[10, 10]} />
        <defs>
          {plotted.map(({ def, points }) => (
            <linearGradient
              key={def.key}
              id={`dist-score-gradient-${def.key}`}
              x1="0"
              y1="0"
              x2="1"
              y2="0"
            >
              {gradientStops(points, top).map((stop) => (
                <stop key={stop.offset} offset={stop.offset} stopColor={stop.color} />
              ))}
            </linearGradient>
          ))}
        </defs>
        {plotted.map(({ def, points }) => (
          <Scatter
            key={`line-${def.key}`}
            data={points}
            shape={() => <g />}
            tooltipType="none"
            legendType="none"
            line={{
              stroke: `url(#dist-score-gradient-${def.key})`,
              strokeWidth: 3.5,
              strokeLinecap: 'round',
            }}
            lineJointType="linear"
            isAnimationActive={false}
          />
        ))}
      </ScatterChart>
    </ResponsiveContainer>
  )
})

// Chart geometry constants — the hover overlay inverts these to map the
// cursor back to (rank, series) WITHOUT any per-point DOM hit targets
// (2,674 overlapping invisible circles made OTS hover unusable).
const CHART_HEIGHT = 280
const PLOT_TOP = 8
const PLOT_RIGHT_PAD = 8
const YAXIS_WIDTH = 40
const XAXIS_HEIGHT = 30

interface PopoverState {
  datum: DistributionPoint
  /** The series the hovered dot belongs to — neighbors come from it. */
  points: DistributionPoint[]
  seriesLabel: string
  left: number
  top: number
  /** The hovered dot's chart position — a visible marker renders here. */
  cx: number
  cy: number
}

export function TaskScoreDistribution({
  tasks,
  model,
  taskPath,
  details,
  series,
  subtitle,
}: TaskScoreDistributionProps): React.JSX.Element | null {
  const navigate = useNavigate()
  const chartWrapRef = useRef<HTMLDivElement | null>(null)
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const rafRef = useRef<number | null>(null)
  const [popover, setPopover] = useState<PopoverState | null>(null)

  // All hover handlers are identity-stable so the memoized chart below
  // never re-renders while the popover opens, moves, or closes.
  const holdPopover = useCallback((): void => {
    if (hideTimerRef.current !== null) {
      clearTimeout(hideTimerRef.current)
      hideTimerRef.current = null
    }
  }, [])
  const releasePopover = useCallback((): void => {
    holdPopover()
    hideTimerRef.current = setTimeout(() => {
      setPopover(null)
    }, POPOVER_HIDE_DELAY_MS)
  }, [holdPopover])
  const openPopover = useCallback(
    (
      datum: DistributionPoint,
      cx: number,
      cy: number,
      points: DistributionPoint[],
      seriesLabel: string
    ): void => {
      holdPopover()
      const width = chartWrapRef.current?.clientWidth ?? POPOVER_WIDTH
      setPopover({
        datum,
        points,
        seriesLabel,
        left: Math.max(0, Math.min(cx + 12, width - POPOVER_WIDTH)),
        top: cy + 14,
        cx,
        cy,
      })
    },
    [holdPopover]
  )
  const openTask = useCallback(
    (taskSlug: string): void => {
      navigate(taskPath(taskSlug))
    },
    [navigate, taskPath]
  )

  const plotted = useMemo(() => {
    const seriesDefs: DistributionSeries[] = series ?? [
      {
        key: 'headline',
        label: 'Average',
        scoreFor: (slug, m) => {
          const task = tasks.find((t) => t.slug === slug)
          return task === undefined ? null : taskScoreFor(task, m)
        },
      },
    ]
    return seriesDefs
      .map((def) => ({ def, points: toPoints(tasks, model, def.scoreFor) }))
      .filter(({ points }) => points.length >= 2)
  }, [series, tasks, model])
  const primary = plotted[0]
  if (primary === undefined) return null
  // Same axis top the chart itself derives, so the hover overlay's geometry
  // lands on the dots rather than beside them.
  const top = plotTop(plotted)

  // Map the cursor to the nearest point: rank from x (points are uniform
  // along x), series by vertical distance. rAF-throttled so dense mouse
  // movement costs at most one state update per frame.
  const onPlotMouseMove = (event: React.MouseEvent<HTMLDivElement>): void => {
    const wrap = chartWrapRef.current
    if (wrap === null || rafRef.current !== null) return
    const { clientX, clientY } = event
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null
      const rect = wrap.getBoundingClientRect()
      const plotWidth = rect.width - YAXIS_WIDTH - PLOT_RIGHT_PAD
      const plotHeight = CHART_HEIGHT - PLOT_TOP - XAXIS_HEIGHT
      const x = clientX - rect.left - YAXIS_WIDTH
      const y = clientY - rect.top - PLOT_TOP
      if (plotWidth <= 0 || x < 0 || x > plotWidth) {
        releasePopover()
        return
      }
      const fraction = x / plotWidth
      let bestPick:
        | {
            datum: DistributionPoint
            def: DistributionSeries
            points: DistributionPoint[]
            cx: number
            cy: number
          }
        | null = null
      let bestDistance = Number.POSITIVE_INFINITY
      for (const { def, points } of plotted) {
        const index = Math.max(
          0,
          Math.min(points.length - 1, Math.round(fraction * (points.length - 1)))
        )
        const datum = points[index]
        if (datum === undefined) continue
        const cy = (1 - datum.score / top) * plotHeight
        const distance = Math.abs(cy - y)
        if (distance < bestDistance) {
          bestDistance = distance
          const cx = (index / Math.max(1, points.length - 1)) * plotWidth
          bestPick = { datum, def, points, cx, cy }
        }
      }
      if (bestPick === null) {
        releasePopover()
        return
      }
      openPopover(
        bestPick.datum,
        bestPick.cx + YAXIS_WIDTH,
        bestPick.cy + PLOT_TOP,
        bestPick.points,
        bestPick.def.label
      )
    })
  }

  const hardCount = primary.points.filter((p) => p.score < 33).length
  const easyCount = primary.points.filter((p) => p.score >= 67).length
  const average = primary.points.reduce((sum, p) => sum + p.score, 0) / primary.points.length
  const modelLabel = model === null ? 'All models' : model

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <CardTitle className="text-sm font-medium text-foreground">
              Task score distribution
            </CardTitle>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {subtitle ?? `Average score per task${model === null ? ' across models' : ''}`}
            </p>
          </div>
          <div className="flex flex-col items-end gap-1">
            <div className="flex items-center gap-1.5 text-xs">
              <span className="rounded-md px-2 py-0.5 tabular-nums" style={{ color: scoreTone(0.1) }}>
                {hardCount} hard
              </span>
              <span className="rounded-md px-2 py-0.5 tabular-nums" style={{ color: scoreTone(0.9) }}>
                {easyCount} easy
              </span>
              <span className="rounded-md border border-border px-2 py-0.5 tabular-nums text-muted-foreground">
                {primary.points.length} scored tasks
              </span>
            </div>
            <p className="font-mono text-xs text-muted-foreground">
              {modelLabel} · {primary.def.label.toLowerCase()} {formatScore(average / 100)} avg
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div ref={chartWrapRef} className="relative">
          <DistributionChart plotted={plotted} />
          {/* One hover overlay instead of thousands of invisible dot
              targets: cursor position -> nearest (rank, series) by math. */}
          <div
            className="absolute cursor-pointer"
            style={{
              left: YAXIS_WIDTH,
              top: PLOT_TOP,
              right: PLOT_RIGHT_PAD,
              bottom: XAXIS_HEIGHT,
            }}
            onMouseMove={onPlotMouseMove}
            onMouseLeave={releasePopover}
            onClick={() => {
              if (popover !== null) navigate(taskPath(popover.datum.slug))
            }}
          />
          {popover !== null && (
            <span
              aria-hidden="true"
              className="pointer-events-none absolute z-10 size-3 rounded-full border-2 border-background shadow-sm"
              style={{
                left: popover.cx - 6,
                top: popover.cy - 6,
                backgroundColor: scoreTone(popover.datum.score / top),
              }}
            />
          )}
          {popover !== null && (
            <div
              className="absolute z-10"
              style={{ left: popover.left, top: popover.top }}
              onMouseEnter={holdPopover}
              onMouseLeave={releasePopover}
            >
              <DistributionPopover
                datum={popover.datum}
                points={popover.points}
                details={details}
                headerLabel={`${popover.seriesLabel} · ${modelLabel}`}
                onOpenTask={openTask}
              />
            </div>
          )}
        </div>
        <div className="mt-1 flex items-baseline justify-between px-10 text-xs text-muted-foreground">
          <span>easiest tasks</span>
          <span>Benchmark tasks sorted by score, high to low</span>
          <span>hardest tasks</span>
        </div>
      </CardContent>
    </Card>
  )
}
